import { useLocalStorageValue } from '@react-hookz/web';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Can } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import {
  useArRemittance,
  useDeleteRemittance,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { RemittanceDetails } from './remittance-details';
import { RemittanceMatchActions } from './RemittanceMatchActions';
import { T } from 'gt-react';

function channelBadge(channel: string) {
  return (
    <Badge variant="outline" className="text-xs">
      {channel.charAt(0).toUpperCase() + channel.slice(1)}
    </Badge>
  );
}

interface RemittanceDetailSlideinProps {
  remittanceId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const DELETE_CONFIRM_MESSAGE = [
  'Delete this remittance?',
  '',
  'This permanently removes the remittance advice, its line items, the linked',
  'payment, any payment applications it produced, and any short-pay',
  'collection threads opened from it. The source email is left in place so',
  'ingestion can be re-run. This action cannot be undone.',
].join('\n');

export function RemittanceDetailSlidein({
  remittanceId,
  isOpen,
  onOpenChange,
}: RemittanceDetailSlideinProps) {
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-remittance-detail-view-size', {
      defaultValue: 50,
    });

  const { remittance, isLoading } = useArRemittance(remittanceId);
  const deleteMutation = useDeleteRemittance();

  async function handleDeleteClick() {
    console.log('[deleteRemittance] Delete button clicked', { remittanceId });
    const ok = window.confirm(DELETE_CONFIRM_MESSAGE);
    console.log('[deleteRemittance] window.confirm result', ok);
    if (!ok) return;
    try {
      const result = await deleteMutation.mutateAsync({ id: remittanceId });
      console.log('[deleteRemittance] mutateAsync resolved', result);
      onOpenChange(false);
    } catch (err) {
      console.error('[deleteRemittance] mutateAsync rejected', err);
      toast.error(
        err instanceof Error
          ? `Failed to delete remittance: ${err.message}`
          : 'Failed to delete remittance',
      );
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        <SheetHeader className="shrink-0 grow-0">
          {isLoading ? (
            <SheetTitle>
              <T context="remittance slide-in loading">Loading...</T>
            </SheetTitle>
          ) : remittance ? (
            <div className="flex items-center gap-2">
              <SheetTitle>
                {remittance.matchedCustomer?.erpDisplayName ||
                  remittance.statedCustomerName?.trim() || (
                    <T context="remittance slide-in title fallback when no customer name">
                      Remittance
                    </T>
                  )}
              </SheetTitle>
              {channelBadge(remittance.channel)}
            </div>
          ) : (
            <SheetTitle>
              <T context="remittance slide-in not found">
                Remittance not found
              </T>
            </SheetTitle>
          )}
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="remittance slide-in loading body">Loading...</T>
            </div>
          ) : remittance ? (
            <RemittanceDetails remittance={remittance} />
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="remittance slide-in not found body">
                Remittance not found
              </T>
            </div>
          )}
        </div>

        <SheetFooter className="shrink-0 grow-0 flex flex-row items-end justify-between border-t gap-2">
          <div className="flex items-center gap-2">
            <Can I={Action.Delete} a={Subject.ARCash}>
              <Button
                variant="destructive"
                onClick={() => void handleDeleteClick()}
                disabled={!remittance || deleteMutation.isPending}
                className="w-fit"
              >
                <T context="delete remittance button">Delete</T>
              </Button>
            </Can>
            <Can I={Action.Update} a={Subject.ARCash}>
              <RemittanceMatchActions
                remittanceId={remittanceId}
                disabled={!remittance}
              />
            </Can>
          </div>
          <Button onClick={() => onOpenChange(false)} className="w-fit">
            <T context="close remittance detail slide-in">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>
    </Sheet>
  );
}
